import { unstable_composeClasses as composeClasses } from '@mui/base';
import { styled, useThemeProps, Grow, Modal, Paper } from '@mui/material';
import { ArrowLeftThin, Clear } from '@ori-ui/icons';
import clsx from 'clsx';
import { forwardRef, useCallback, useMemo } from 'react';
import IconButton from '../IconButton';
import useAutoclose from '../useAutoclose';
import { capitalize, rootShouldForwardProp } from '../utils';
import { getDialogBaseUtilityClass } from './dialogBaseClasses';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const getDialogBaseSlots = ownerState => {
  const {
    size
  } = ownerState;
  return {
    root: ['root', `size${capitalize(size)}`],
    backButton: ['backButton'],
    closeButton: ['closeButton'],
    dialogGrid: ['dialogGrid']
  };
};

const useUtilityClasses = ownerState => {
  const {
    classes
  } = ownerState;
  const slots = getDialogBaseSlots(ownerState);
  const composedClasses = composeClasses(slots, getDialogBaseUtilityClass, classes);
  return { ...classes,
    ...composedClasses
  };
};

const DialogBaseRoot = styled(Modal, {
  name: 'MuiOriDialogBase',
  slot: 'Root',
  overridesResolver: ({
    ownerState
  }, styles) => [styles.root, styles[`size${capitalize(ownerState.size)}`]],
  shouldForwardProp: prop => rootShouldForwardProp(prop) || prop === 'classes'
})({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  outline: 0
});
const DialogGrid = styled(Paper, {
  name: 'MuiOriDialogBase',
  slot: 'DialogGrid',
  skipSx: true,
  overridesResolver: (_, styles) => [styles.dialogGrid]
})(({
  theme,
  ownerState
}) => ({
  display: 'grid',
  justifyItems: 'center',
  position: 'relative',
  minWidth: 320,
  maxWidth: '60vw',
  minHeight: 100,
  maxHeight: `calc(100vh - ${theme.spacing(32)})`,
  outline: 0,
  padding: theme.spacing(8, 6, 6, 6),
  ...(ownerState.size === 'small' && {
    padding: 0
  }),
  overflow: 'hidden',
  [theme.breakpoints.only('xs')]: {
    boxSizing: 'border-box',
    minWidth: '100vw',
    maxWidth: '100vw',
    maxHeight: `calc(100vh - ${theme.spacing(12)})`,
    padding: theme.spacing(8, 3, 3, 3),
    ...(ownerState.size === 'small' && {
      padding: 0
    }),
    position: 'absolute',
    bottom: 0,
    left: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0
  }
}));
const BackButton = styled(IconButton, {
  name: 'MuiOriDialogBase',
  slot: 'BackButton',
  skipSx: true,
  overridesResolver: (_, styles) => [styles.backButton]
})(({
  theme
}) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  padding: theme.spacing(1),
  margin: theme.spacing(2),
  background: 'rgba(255,255,255,.75)'
}));
const CloseButton = styled(IconButton, {
  name: 'MuiOriDialogBase',
  slot: 'CloseButton',
  skipSx: true,
  overridesResolver: (_, styles) => [styles.backButton]
})(({
  theme
}) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  padding: theme.spacing(1.4),
  margin: theme.spacing(2),
  background: 'rgba(255,255,255,.75)'
}));
export const DialogBase = /*#__PURE__*/forwardRef(function DialogBase(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiOriDialogBase'
  });
  const {
    children,
    open,
    hideCloseButton,
    showBackButton,
    onBack,
    backButton: _,
    className,
    disableTransition,
    size = 'medium',
    timeoutMs = 2000,
    enableAutoClose,
    ...other
  } = props;
  const {
    onClose
  } = other;
  const ownerState = useMemo(() => ({ ...props,
    size
  }), [props, size]);
  const classes = useUtilityClasses(ownerState);
  const handleClose = useCallback(event => {
    onClose?.(event ?? {}, 'escapeKeyDown');
  }, [onClose]);
  useAutoclose({
    autoClose: enableAutoClose,
    open,
    timeoutMs,
    onClose: handleClose
  });
  const handleBackClick = useCallback(() => {
    onBack?.();
  }, [onBack]);
  const {
    backButton = /*#__PURE__*/_jsx(BackButton, {
      ownerState: ownerState,
      variant: "text",
      color: "default",
      className: classes.backButton,
      onClick: handleBackClick,
      children: /*#__PURE__*/_jsx(ArrowLeftThin, {
        color: "inherit"
      })
    })
  } = props;
  return /*#__PURE__*/_jsx(DialogBaseRoot, {
    ref: ref,
    closeAfterTransition: true,
    ownerState: ownerState,
    className: clsx(classes.root, className),
    open: open,
    ...other,
    children: /*#__PURE__*/_jsx(Grow, {
      in: open,
      children: /*#__PURE__*/_jsxs(DialogGrid, {
        ownerState: ownerState,
        className: classes.dialogGrid,
        children: [showBackButton && backButton, !hideCloseButton && /*#__PURE__*/_jsx(CloseButton, {
          ownerState: ownerState,
          variant: "text",
          color: "default",
          className: classes.closeButton,
          onClick: handleClose,
          children: /*#__PURE__*/_jsx(Clear, {
            fontSize: "small",
            color: "inherit"
          })
        }), children]
      })
    })
  });
});
export default DialogBase;